// InterfaceSection.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { LANDING_SECTIONS } from './landingConstants';
import { SectionOuter, SectionInner as BaseSectionInner, landingSpacing } from './LandingLayout';
import ScrollReveal from './ScrollReveal';

// Rythme vertical + conteneur 1200px communs à toutes les sections
// (voir LandingLayout.jsx). Ici on l'étend en colonne flex avec un grand
// gap : c'est l'espacement entre chaque grand bloc de démo (journal, amis,
// poids, sommeil).
const Section = styled(SectionOuter)``;
const dataEntry = keyframes`
  0% { opacity: 0; transform: translateY(14px); }
  8%, 100% { opacity: 1; transform: translateY(0); }
`;
const progressEntry = keyframes`
  0% { transform: scaleX(0); }
  40%, 100% { transform: scaleX(1); }
`;
const sliceEntry = keyframes`
  0% { opacity: 0; transform: scale(0.72); }
  45%, 100% { opacity: 1; transform: scale(1); }
`;
const chartLineDraw = keyframes`
  0% { stroke-dashoffset: 1; }
  24%, 100% { stroke-dashoffset: 0; }
`;
const barEntry = keyframes`
  0% { transform: scaleY(0.04); opacity: 0.25; }
  12%, 100% { transform: scaleY(1); opacity: 1; }
`;

const SectionInner = styled(BaseSectionInner)`
  display: flex;
  flex-direction: column;
  gap: ${landingSpacing.gap.xxl};
`;

// Bloc titre + démo : remplace l'ancien style inline (gap non harmonisé
// avec le reste des espacements de la page).
const InterfaceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${landingSpacing.gap.lg};
  width: 100%;
`;

const SectionIntro = styled.div`
  margin-bottom: ${landingSpacing.gap.md};

  h2 {
    margin: 0;
    color: #1a2e40;
    font-family: 'Poppins', 'Roboto', sans-serif;
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    line-height: 1.15;
    text-align: center;
  }

  p {
    max-width: 680px;
    margin: ${landingSpacing.gap.sm} auto 0;
    color: #344b61;
    font-size: 1.05rem;
    line-height: 1.65;
    text-align: center;
  }
`;

const InterfaceText = styled.div`
  text-align: left;
  width: 100%;

  h2 {
    font-family: 'Poppins', 'Roboto', sans-serif;
    font-size: clamp(1.4rem, 2.2vw, 2rem);
    font-weight: 700;
    color: #1a2e40;
    letter-spacing: -0.01em;
    margin: 0 0 ${landingSpacing.gap.sm} 0;
    line-height: 1.15;
  }

  p {
    font-size: clamp(1rem, 1.3vw, 1.2rem);
    line-height: 1.7;
    color: #344b61;
    max-width: 600px;
    margin: 0;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    text-align: center;
    p {
      max-width: 100%;
      margin: 0 auto;
    }
  }
`;

// === On supprime le padding et la marge de JournalDemo pour aligner ===
const DemoWrapper = styled.div`
  width: 100%;

  &[data-demo-id='journal'] .journal-container.demo-mode .meal-table tbody > tr.meal-table-row,
  &[data-demo-id='journal'] .journal-container.demo-mode .mobile-diary-meal .food-entry {
    animation: ${dataEntry} 5200ms ease-out infinite both;
  }

  &[data-demo-id='friends'] .landing-friend-entry {
    animation: ${dataEntry} 5200ms ease-out infinite both;
  }

  &[data-demo-id='weight'] .weight-demo-chart-line {
    stroke-dasharray: 1;
    animation: ${chartLineDraw} 6500ms ease-in-out infinite both;
  }

  &[data-demo-id='sleep'] .sleep-demo-history-bar {
    transform-box: fill-box;
    transform-origin: bottom;
    animation: ${barEntry} 6000ms ease-out infinite both;
  }

  &[data-demo-id='journal'] .summary-chart-slice {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${sliceEntry} 5200ms ease-out infinite both;
  }

  &[data-demo-id='journal'] .progress-bar-fill {
    transform-origin: left center;
    animation: ${progressEntry} 5200ms ease-out infinite both;
  }

  &[data-demo-id='weight'] .landing-weight-session-entry,
  &[data-demo-id='sleep'] .landing-sleep-session-entry,
  &[data-demo-id='sleep'] .landing-sleep-live-entry {
    animation: ${dataEntry} 5200ms ease-out infinite both;
  }

  &[data-demo-id='journal'] .journal-container.demo-mode .meal-table tbody > tr.meal-table-row:nth-of-type(2),
  &[data-demo-id='journal'] .journal-container.demo-mode .mobile-diary-meal .food-entry:nth-child(2),
  &[data-demo-id='friends'] .landing-friends-grid > .landing-friend-entry:nth-child(2),
  &[data-demo-id='weight'] .landing-weight-session-entry:nth-child(2),
  &[data-demo-id='sleep'] .landing-sleep-session-entry:nth-child(2) { animation-delay: 350ms; }
  &[data-demo-id='journal'] .journal-container.demo-mode .meal-table tbody > tr.meal-table-row:nth-of-type(3),
  &[data-demo-id='journal'] .journal-container.demo-mode .mobile-diary-meal .food-entry:nth-child(3),
  &[data-demo-id='friends'] .landing-friends-grid > .landing-friend-entry:nth-child(3),
  &[data-demo-id='weight'] .landing-weight-session-entry:nth-child(3),
  &[data-demo-id='sleep'] .landing-sleep-session-entry:nth-child(3) { animation-delay: 700ms; }
  &[data-demo-id='journal'] .journal-container.demo-mode .meal-table tbody > tr.meal-table-row:nth-of-type(4) { animation-delay: 1050ms; }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation: none !important; }
  }

  .journal-container {
    padding: 0 !important;
    margin: 0 !important;
  }
`;

const InterfaceSection = ({ variant = 'desktop' }) => {
  const isMobileVariant = variant === 'mobile';

  return (
    <Section id="interface">
      <SectionInner>
        <SectionIntro>
          <h2>Votre santé, à votre rythme</h2>
          <p>Suivez votre alimentation, votre sommeil et votre poids. Partagez vos progrès avec les personnes qui vous motivent.</p>
        </SectionIntro>
        {LANDING_SECTIONS.map((section) => {
          const DemoComponent = section.component;
          const demoProps = section.id === 'journal'
            ? { compact: isMobileVariant }
            : section.id === 'tracking'
              ? { variant }
              : {};

          return (
            <ScrollReveal key={section.id}>
              <InterfaceBlock>
                <InterfaceText>
                  <h2>{section.title}</h2>
                </InterfaceText>
                <DemoWrapper data-demo-id={section.id}>
                  <DemoComponent {...demoProps} />
                </DemoWrapper>
              </InterfaceBlock>
            </ScrollReveal>
          );
        })}
      </SectionInner>
    </Section>
  );
};

export default InterfaceSection;
